import { useState } from 'react'

import { omit } from 'lodash'
import { DateTime } from 'luxon'

import { SimpleTable, TableSkeleton, Alert, SeverityPill, AssetDetailModal } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import alarmLocationDrilldown from '@/insights/common/alarmLocationDrilldown'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import LiveDuration from '@/insights/components/LiveDuration'
import { getSeverityLabel } from '@/insights/components/SeverityDropdown'
import {
  Alarm,
  ApiService,
  InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
  type InsightDefinition,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'

export interface ReeferAlarmsAlarmsParams extends InsightParametersType {
  companyId?: number
  status?: string
  severity?: string[]
  code?: string
  codeId?: number[]
  date?: string
  from?: string
  to?: string
  isDrilldown?: boolean
}

const statusOptions = [
  { label: 'All', value: '', isDefault: true },
  { label: 'Active', value: 'active' },
  { label: 'Cleared', value: 'cleared' },
]

const ReeferAlarmDetails: InsightDefinition<ReeferAlarmsAlarmsParams, Alarm[]> = {
  active: true,
  slug: 'alarm/alarms-detail',
  category: 'reeferAlarm',
  name: 'Reefer Alarms Detail',
  description: 'List alarm details, optionally filtered by status, severity, and type',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,
  refreshInterval: 300000,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      if (field === 'codeId') {
        return { ...parameters, severity: undefined }
      }
      if (field === 'severity') {
        return { ...parameters, codeId: undefined }
      }
      return parameters
    },
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'status',
        control: 'CustomSingleSelect',
        label: 'Alarm Status',
        options: statusOptions,
      },
      { id: 'note', control: 'Label', label: 'Choose either Severity or Alarm Code' },
      { id: 'codeId', control: 'AlarmCodeDropdown', required: false, label: 'Alarm', showCode: true },
      { id: 'severity', control: 'SeverityDropdown' },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions,
      },
      {
        id: 'customDate',
        label: 'Raised Time',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (configuration: ReeferAlarmsAlarmsParams) => {
      if (configuration.date === 'custom' && !configuration.from && !configuration.to) {
        return 'Enter the custom date for Raised Time'
      }
      if (configuration.date === 'custom') {
        if (configuration.from && configuration.to) {
          const fromDate = new Date(configuration.from)
          const toDate = new Date(configuration.to)
          if (fromDate > toDate) {
            return ' Start date must be before end date'
          }
        }
        const dateValidation = validateDateRangeWithin13Months(configuration.from, configuration.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }
      const hasCodeId = configuration.codeId && configuration.codeId.length > 0
      const hasSeverity = configuration.severity && configuration.severity.length > 0

      if (configuration?.companyId && (hasCodeId || hasSeverity)) {
        return true
      }
      if (configuration?.companyId) {
        return 'Alarm Code or Severity is required'
      }
      return 'Company and either Alarm Code or Severity are required.'
    },
  },
  csv: async (api: ApiService, parameters: ReeferAlarmsAlarmsParams, title: string) => {
    const path = '/insights/alarms/alarms-detail'
    await downloadCsv(api, path, parameters, title)
  },
  load: async (api: ApiService, parameters: ReeferAlarmsAlarmsParams) => {
    const params = { ...parameters }
    const path = `/insights/alarms/alarms-detail?${buildQuery(params)}&skip=0&take=1000`
    const response = await api.get<Alarm[]>({
      path,
      origin: 'AlarmsBySeverity#loader',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }

    if (showSkeleton) {
      // This could be handled by the framework by just returning the
      // same status. But we should make the skeleton match the final
      // table result if possible.
      return <TableSkeleton columns={6} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No alarms found for severity critical".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }
    const parameters = configuration.parameters as ReeferAlarmsAlarmsParams
    const uniqueCompanies = new Set(data.map((alarm) => alarm.company.id))

    const shouldShowCompany = (parameters.isDrilldown && uniqueCompanies.size > 1) || uniqueCompanies.size > 1

    const assetInfo = parameters.assetIds && data.length > 0 ? `${data[0].company.name} ` : null

    const body = data
      ?.sort((a, b) => {
        const t = b.raisedTime.getTime() - a.raisedTime.getTime()
        if (t === 0) {
          return a.asset.name.localeCompare(b.asset.name)
        }
        return t
      })

      .map((alarm) => ({
        ...(shouldShowCompany && {
          Company: <span>{alarm.company.name}</span>,
        }),

        Asset: (
          <button
            onClick={() => setSelectedAsset(alarm.asset.id)}
            className="text-left block text-blue-500 cursor-pointer"
          >
            {alarm.asset.name}
          </button>
        ),
        Alarm: (
          <span className="block text-left">
            {alarm.alarmCode.description} ({alarm.alarmCode.code})
          </span>
        ),
        Raised: {
          id: `raised-${alarm.id}`,
          date: alarm.raisedTime,
          display: DateTime.fromJSDate(alarm.raisedTime).toFormat('yyyy-MM-dd HH:mm'),
        },

        Cleared:
          configuration.parameters.status !== 'active' && alarm.clearedTime
            ? DateTime.fromJSDate(alarm.clearedTime).toFormat('yyyy-MM-dd HH:mm')
            : '',

        Duration: {
          value: (alarm.clearedTime || new Date()).getTime() - alarm.raisedTime.getTime(),
          display: (
            <LiveDuration
              raisedTime={alarm.raisedTime}
              clearedTime={alarm.clearedTime}
              className="block text-left min-w-fit"
            />
          ),
        },
        Severity: {
          value: alarm.alarmCode.severityCode,
          display: (
            <SeverityPill
              label={getSeverityLabel(String(alarm.alarmCode.severityCode))}
              severityCode={alarm.alarmCode.severityCode}
            />
          ),
        },

        Geofence: <span className="block text-left">{alarm.raisedGeofence ? alarm.raisedGeofence.name : ''}</span>,
        'Raised At': alarmLocationDrilldown(alarm, configuration, 'raised', parameters.companyId),
        ...(parameters.status === 'cleared' && {
          'Cleared At': alarmLocationDrilldown(alarm, configuration, 'cleared', parameters.companyId),
        }),
      }))

    let body2
    if (configuration.parameters.status === 'active') {
      body2 = body.map((row) => omit(row, 'Cleared'))
    }

    const dataItem = (body2 || body)?.[0]

    const headers = dataItem
      ? Object.keys(dataItem)
      : Array(3)
          .fill(null)
          .map((_, index) => `placeholder-${index}`)

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        {assetInfo && <div className="px-2 py-1 text-lg font-medium">{assetInfo}</div>}
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body2 || body} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()

    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return 'Error'
    }
    const fields: Record<string, string> = {}
    const params = configuration.parameters as ReeferAlarmsAlarmsParams

    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.status) {
      const { status } = params
      const statusDisplay = statusOptions.find((o) => status === o.value)?.label
      if (statusDisplay) {
        fields['Status'] = statusDisplay
      }
    }

    if (params.severity?.length) {
      const p = typeof params.severity === 'string' ? [params.severity] : params.severity
      const labels = p
        .map((s) => getSeverityLabel(s))
        .filter((l) => !!l)
        .join(', ')
      fields['Severity'] = labels
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING && data === undefined) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferAlarmDetails
